import React from 'react';
import { Moment } from 'moment-timezone';
import { CheckboxProps, Form } from 'semantic-ui-react';

import { FormDatePicker } from '@modules/common/components/Field';
import { styled } from '@modules/common/components/ThemeProvider/utils';
import { getDaysString } from '@src/lib/date';
import config from '@config/config';
import Checkbox from '@modules/common/components/Checkbox';

import {
  MAX_EXPIRY_DURATION_DAYS,
  getDefaultExpiryDate,
  isDateDisabled,
} from '../../ClientForm/utils';
import Warning from './Warning';

type Props = {
  isLoading: boolean;
  expiryDate?: Moment;
  setExpiryDate: (date: Moment | undefined) => void;
  expirationDisabled: boolean;
  setExpirationDisabled?: (isDisabled: boolean) => void;
  hasAcceptedExpiryRisk?: boolean;
  setHasAcceptedExpiryRisk?: (value: boolean) => void;
  readonly?: boolean;
};

function ExpiryDateSelector({
  isLoading,
  expiryDate,
  setExpiryDate,
  expirationDisabled,
  setExpirationDisabled,
  hasAcceptedExpiryRisk,
  setHasAcceptedExpiryRisk,
  readonly = false,
}: Props) {
  function onSelectNoExpiry(_: any, data: CheckboxProps) {
    setExpirationDisabled!(Boolean(data.checked));

    if (data.checked) {
      setExpiryDate(undefined);
    }
  }

  function getDisplayedExpiryDate() {
    if (!expiryDate && !expirationDisabled) {
      return getDefaultExpiryDate().toDate();
    }
    return expiryDate;
  }

  function getExpiryDateString() {
    if (expirationDisabled) {
      return 'Client will not expire.';
    }
    if (expiryDate) {
      return `Client will expire ${getDaysString(expiryDate.toDate(), false)}.`;
    }
    return `Client will expire in ${MAX_EXPIRY_DURATION_DAYS} days`;
  }

  if (!config.oAuthClientsExpiryEnabled) {
    return null;
  }
  const dateInputEnabled = !isLoading && !readonly && !expirationDisabled;
  return (
    <>
      <StyledFormGroup inline>
        <StyledDateInfoWrapper>
          <FormDatePicker
            fieldName="expiryDate"
            label="Expiry date:"
            date={getDisplayedExpiryDate()}
            handleChange={d => setExpiryDate(d)}
            disabledDates={isDateDisabled}
            errors={{}}
            inputEnabled={dateInputEnabled}
            required={false}
            style={{
              pointerEvents: dateInputEnabled ? 'auto' : 'none',
              opacity: dateInputEnabled ? '1' : '0.45',
              minWidth: '260px',
              paddingRight: 0,
            }}
            disabled={dateInputEnabled}
            showTime
          />
          <StyledExpirationString>
            <i>{getExpiryDateString()}</i>
          </StyledExpirationString>
        </StyledDateInfoWrapper>
        <StyledCheckboxWrapper>
          <Checkbox
            label="Never expire"
            checked={expirationDisabled}
            onChange={onSelectNoExpiry}
            disabled={readonly}
          />
        </StyledCheckboxWrapper>
      </StyledFormGroup>
      {expirationDisabled && !readonly && (
        <Warning
          hasAcceptedExpiryRisk={hasAcceptedExpiryRisk}
          setHasAcceptedExpiryRisk={setHasAcceptedExpiryRisk}
        />
      )}
    </>
  );
}

export default ExpiryDateSelector;

const StyledFormGroup = styled(Form.Group)``;

const StyledDateInfoWrapper = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledExpirationString = styled('div')`
  margin-top: 18px;
  width: 180px;
`;

const StyledCheckboxWrapper = styled('div')`
  margin-top: 18px;
`;
